/* eslint-disable no-shadow, no-unused-vars, no-restricted-globals, func-names */
/**
 * The OData plugin module
 * @module component/edm-odata/index
 */
import _ from 'underscore';
import {
  hasOwnProperty,
  defineConstProperty,
  defineProducedPropertyOnClass,
} from '@bingads-webui/reflection';

import { odata } from '@bingads-webui/http-util';
import type { ODataMethod } from '@bingads-webui/http-util';
import * as url from '@bingads-webui/url-util';
import { resIdsPlugin } from '@bingads-webui/edm-resource-identifiers';
import { odataCachePlugin } from './cache';
import type {
  IOrderBy,
  IFilter,
  IODataResponse,
  IEdm,
  IEdmResource,
  IOrderByArrayElement,
  IOdataCollectionResource,
  IOdataEntityResource,
  IOdataNavigation,
  IOdataResource,
  LegacyFindAllQuery,
  ODataActionOptions,
  ODataCallableOptions,
  ODataCallOptions,
  ODataCollectionOptions,
  ODataEntity,
  ODataOptions,
  ODataPluginProcessOptions,
  IODataResourceIdentifier,
} from './types';

// support a mongoDB like orderby object for now: { age : -1, name: 1 }
// for backward compatibility, support array format: [['age', 'desc'], ['name', 'asc']]
function parseOrderBy(orderby: IOrderBy | undefined) {
  function lowerCaseOrderValue(value: IOrderByArrayElement): IOrderByArrayElement {
    return _.map(value, (v: string, index: number) => (index === 1 ? v.toLowerCase() : v)) as IOrderByArrayElement;
  }

  if (_.isEmpty(orderby)) {
    return {};
  }

  let $orderby: string;
  if (_.isArray(orderby)) {
    $orderby = _.map(orderby, (value: IOrderByArrayElement) => lowerCaseOrderValue(value).join(' ')).join(',');
  } else {
    $orderby = _.map(orderby, (value: number, key: string) => `${key} ${value > 0 ? 'asc' : 'desc'}`).join(',');
  }

  return {
    $orderby,
  };
}

// TODO: [yaoyao] add support to parse filter object
function parseFilter(filter: IFilter | undefined) {
  if (_.isEmpty(filter) || _.isEmpty(filter!.where)) {
    return {};
  }

  return {
    $filter: filter!.where,
  };
}

function parseTop(top: number | undefined) {
  if (!top) {
    return {};
  }

  return {
    $top: top,
  };
}

function parseSkip(skip: number | undefined) {
  if (!skip) {
    return {};
  }

  return {
    $skip: skip,
  };
}

function parseCount(count: boolean | undefined) {
  if (count === undefined) {
    return {};
  }

  return {
    $count: count,
  };
}

function parseSelect(select: string[] | null | undefined, resource: Pick<IOdataResource, 'type'>) {
  if (!select) return {};
  const parsed: { [key: string]: string } = {};
  const elementType = resource.type.elementType ? resource.type.elementType : resource.type;
  const navigationPropertyNames = elementType.navigationPropertyNames || [];

  // Separate select items into different categories
  const simpleSelectProps: string[] = [];
  const simpleExpandProps: string[] = [];
  const nestedNavProps: { [navProp: string]: string[] } = {};

  select.forEach((item: string) => {
    if (_.contains(navigationPropertyNames, item)) {
      // Simple navigation property (backward compatibility)
      simpleExpandProps.push(item);
    } else if (item.includes('/')) {
      // Check if item contains navigation property path (e.g., "Campaign/Name" or "Campaign/*")
      const [navProp, ...rest] = item.split('/');
      const nestedProp = rest.join('/');

      // Check if the first part is a navigation property
      if (_.contains(navigationPropertyNames, navProp)) {
        // Initialize array for this navigation property if not exists
        if (!nestedNavProps[navProp]) {
          nestedNavProps[navProp] = [];
        }

        // Only add the nested property if it's not a wildcard
        // Wildcards ("*") will result in expanding without $select clause
        if (nestedProp && nestedProp !== '*') {
          nestedNavProps[navProp].push(nestedProp);
        }
      } else {
        // Not a navigation property, treat as regular select
        simpleSelectProps.push(item);
      }
    } else {
      // Regular property
      simpleSelectProps.push(item);
    }
  });

  // Build $select clause with non-navigation properties
  if (simpleSelectProps.length) {
    parsed.$select = simpleSelectProps.join(',');
  }

  // Build $expand clause: navigation properties with nested selects first, then simple expands
  const expandParts: string[] = [];

  // Add navigation properties with nested selects first
  Object.keys(nestedNavProps).forEach((navProp) => {
    const nestedProps = nestedNavProps[navProp];
    if (nestedProps.length > 0) {
      // Generate: NavProp($select=Prop1,Prop2,...)
      expandParts.push(`${navProp}($select=${nestedProps.join(',')})`);
    } else {
      // Wildcard case: just expand without $select clause
      expandParts.push(navProp);
    }
  });

  // Add simple expands (backward compatibility)
  expandParts.push(...simpleExpandProps);

  if (expandParts.length) {
    parsed.$expand = expandParts.join(',');
  }

  return parsed;
}

function parseSearch(search: string | undefined) {
  if (!search) {
    return {};
  }
  return { $search: search };
}

// TODO: make all the legacy findall call related logic to a separate edm plugin package
// backward compatibility for legacy grid jsdata resource finall call
const legacyJsDataResultProcess = (response: IODataResponse<ODataEntity>) => {
  const result = response.value.slice();
  Object.defineProperty(result, 'totalCount', {
    value: response['@odata.count'],
    enumerable: false,
  });
  Object.defineProperty(result, 'raw', {
    value: response,
    enumerable: false,
  });
  return result;
};

/**
 * @param {EDM} edm - The EDM object to apply the plugin to
 * @param {String} rootURL - The root URL of the OData service
 * @param {object} dependants - The entity dependency relations
 * @param {object} processOptions - The options to process response and error
 * @return {Void} nothing to return
 */
export function odataPlugin(edm: IEdm, rootURL: string, dependants: Record<string, string[]> = {}, {
  processResponse = _.identity,
  processError = _.identity,
  processOptions = _.identity,
}: ODataPluginProcessOptions = {}) {
  const odataRootURL = rootURL.replace(/\/*$/, '');
  if (hasOwnProperty(edm, 'odata')) {
    return;
  }

  function wrapODataMethod<TResult = unknown>(
    method: ODataMethod,
    resource: IEdmResource
  ) {
    return function wrappedODataMethod(odataURL: string, options?: ODataOptions) {
      return method<TResult>(odataURL, processOptions(options, resource))
        .then(response => processResponse(response, resource) as TResult)
        .catch((error: unknown) => {
          throw processError(error, resource);
        });
    };
  }

  odataCachePlugin(edm, dependants, {
    fetchEntity: (resource: IOdataEntityResource, {
      select,
      ...others
    }: ODataOptions & {
      select?: string[];
    }) => wrapODataMethod(odata.get, resource as IEdmResource)(resource.odataURL, {
      data: {
        ...parseSelect(select, resource),
        ...others,
      },
    }),
    patchEntity: (
      resource: IOdataEntityResource,
      {
        select,
      }: { select?: string[] },
      update: ODataEntity
    ) => wrapODataMethod(odata.patch, resource as IEdmResource)(resource.odataURL, {
      data: update,
      urlParameters: {
        ...parseSelect(select, resource),
      },
    }),
    createEntity: (
      collectionResource: IOdataCollectionResource,
      {
        select,
      }: { select?: string[] },
      data: ODataEntity
    ) => wrapODataMethod<ODataEntity>(odata.post, collectionResource as IEdmResource)(
      collectionResource.odataURL,
      {
        data,
        urlParameters: {
          ...parseSelect(select, collectionResource),
        },
      }
    ),
    deleteEntity: (resource: IOdataEntityResource) => wrapODataMethod(
      odata.delete,
      resource as IEdmResource
    )(resource.odataURL),
    fetchCollection: (
      collectionResource: IOdataCollectionResource,
      {
        top,
        skip,
        count,
        orderby,
        filter,
        select,
        search,
        ...others
      }: ODataCollectionOptions
    ) => wrapODataMethod(odata.get, collectionResource as IEdmResource)(collectionResource.odataURL, {
      data: {
        $top: top,
        $skip: skip,
        $count: count,
        ...parseOrderBy(orderby),
        ...parseFilter(filter),
        ...parseSelect(select, collectionResource),
        ...parseSearch(search),
        ...others,
      },
    }),
    action: (
      collectionResource: IOdataResource,
      {
        top,
        skip,
        count,
        orderby,
        filter,
        select,
        payload,
        customParameters,
      }: ODataActionOptions
    ) => wrapODataMethod(odata.post, collectionResource as IEdmResource)(collectionResource.odataURL, {
      data: payload,
      urlParameters: {
        ...parseTop(top),
        ...parseSkip(skip),
        ...parseCount(count),
        ...parseOrderBy(orderby),
        ...parseFilter(filter),
        ...parseSelect(select, collectionResource),
        ...customParameters,
      },
    }),
  });

  const {
    getEntity,
    postEntity,
    destroyEntityAsPromise,
    entityAction,
    getCollection,
    collectionAction,
  } = edm['odata.cache'];

  resIdsPlugin(edm);

  /**
   * @name edm
   * @property {Object} odata - A dummy indicator for odataPlugin being applied
   */
  defineConstProperty(edm, 'odata', (() => {
    const {
      ResourceIdentifier,
      CollectionResourceIdentifier,
      Navigation,
    } = edm.resourceIdentifiers;

    defineProducedPropertyOnClass(
      ResourceIdentifier,
      'odataURL',
      /**
       * @memberof ResourceIdentifier#
       * @param {IODataResourceIdentifier} this - Resource identifier instance
       * @returns {String} The URL to access the OData resource
       */ function (this: IODataResourceIdentifier) {
        return url.join(odataRootURL, this.path);
      }
    );

    // edm.currentCustomer.Accounts is an instance of ResourceIdentifier
    // usage:
    // edm.currentCustomer.Accounts.get({ top: 20, skip: 10 })
    // TODO:
    //   * type enrichment, for example, 'Active' => Enum.CampaignStatus'Active'
    //   * type check
    //   * cache and onChange event on response
    CollectionResourceIdentifier.prototype.get = function get({
      top = 20,
      skip = 0,
      count = true,
      // when null means default response
      // most likely it would includes all non navigation property and exclude all navigation ones
      select = null,
      //   we should avoid random query params like SegmentationTypes, startDate, endDate, etc
      filter = null,
      orderby = null,
      search = null,
      ...others
    }: ODataCollectionOptions = {}, hardResetData?: unknown, bypassCache?: boolean) {
      return getCollection(
        this,
        {
          top,
          skip,
          count,
          select,
          filter,
          orderby,
          search,
          ...others,
        },
        hardResetData,
        bypassCache
      );
    };
    ResourceIdentifier.prototype.get = function get({
      // when null means default response
      // most likely it would includes all non navigation property and exclude all navigation ones
      select = null,
      ...others
    }: ODataOptions & { select?: string[] | null } = {}, hardResetData?: unknown, bypassCache?: boolean) {
      return getEntity(
        this,
        {
          select,
          ...others,
        },
        hardResetData,
        bypassCache
      );
    };

    ResourceIdentifier.prototype.patch = function patch(data: ODataEntity, {
      select = null,
    }: { select?: string[] | null } = {}, bypassCache?: boolean) {
      const cacheItem = getEntity(
        this,
        {
          select,
        },
        undefined,
        bypassCache
      );
      return cacheItem.update(data);
    };

    // NOTE: likezh: DELETE operation returns a Promise, not an observable.
    // See comments in cache.js.
    ResourceIdentifier.prototype.destroyAsPromise = function destroy() {
      return destroyEntityAsPromise(this);
    };

    ResourceIdentifier.prototype.$_callableMakeCall = function $callableMakeCall({
      top,
      skip,
      count,
      orderby = null,
      filter = null,
      select = null,
      payload,
      hardResetData,
      bypassCache,
      // custom query options (forwarded to URL query string for both FunctionType and ActionType)
      startDate,
      endDate,
      customParameters,
    }: ODataCallableOptions = {}) {
      if (this.type instanceof edm.types.CollectionType) {
        if (this.navigation.source.type instanceof edm.types.ActionType) {
          return collectionAction(this, {
            top: top || 20,
            skip: skip || 0,
            count: count === undefined ? true : count,
            orderby,
            filter,
            select,
            payload,
            customParameters,
          }, bypassCache);
        }
        if (this.navigation.source.type instanceof edm.types.FunctionType) {
          return getCollection(this, {
            top: top || 20,
            skip: skip || 0,
            count: count === undefined ? true : count,
            orderby,
            filter,
            select,
            startDate,
            endDate,
          }, hardResetData, bypassCache);
        }
      }

      // In theory, we should not have param like top, count, filter and orderby
      // for none collectionType. But the Odata metadata have some incorrect information
      // that return type of collection is marked as none collection
      if (this.navigation.source.type instanceof edm.types.ActionType) {
        return entityAction(this, {
          top,
          skip,
          count,
          orderby,
          filter,
          select,
          payload,
        }, bypassCache);
      }

      return getEntity(this, {
        top,
        skip,
        count,
        orderby,
        filter,
        select,
      }, hardResetData, bypassCache);
    };

    ResourceIdentifier.prototype.$makeCall = function $makeCall({
      query,
      functionParams,
      payload,
      hardResetData,
      bypassCache,
    }: ODataCallOptions = {}) {
      return this.$call(functionParams).$_callableMakeCall({
        ...query,
        payload,
        hardResetData,
        bypassCache,
      });
    };

    // align with jsdata, to ensure backward compatibility with jsdata data source.
    ResourceIdentifier.prototype.findAll = function findAll(query: LegacyFindAllQuery = {}) {
      if (this.type instanceof edm.types.ActionType) {
        return this.$makeCall({
          query: {
            top: query.limit || 20,
            skip: query.offset || 0,
            count: true,
            orderby: query.orderBy,
            filter: query.filter,
            select: query.select,
          },
          payload: query.query,
        }).toPromise().then(legacyJsDataResultProcess);
      }
      throw new Error('findall call for this type is not suported');
    };

    defineProducedPropertyOnClass(CollectionResourceIdentifier, 'idAttribute', function idAttribute(this: IODataResourceIdentifier) {
      return this.type.elementType!.key;
    });

    CollectionResourceIdentifier.prototype.post = function post(data: ODataEntity, {
      select = null,
    } = {}) {
      return postEntity(this, {
        select,
      }, data);
    };

    // usage:
    // edm.currentCustomer.Accounts.findAll({ limit: 20 }), align with jsdata
    // TODO:
    //   * refine available options, jsdata aligned arguments
    CollectionResourceIdentifier.prototype.findAll = function findAll(query: LegacyFindAllQuery = {}) {
      return this.get({ top: query.limit }).toPromise().then(legacyJsDataResultProcess);
    };

    // usage:
    // edm.currentCustomer.Accounts.update(123, { Name: 'another name' }), align with jsdata
    CollectionResourceIdentifier.prototype.update = function update(id: string | number, data: ODataEntity) {
      return this.$withKey(id).patch(data).toPromise();
    };

    defineProducedPropertyOnClass(
      Navigation,
      'odataURL',
      /**
       * @memberof Navigation#
       * @param {IOdataNavigation} this - Navigation instance
       * @returns {String} The URL to access the OData resource
       */
      function (this: IOdataNavigation) {
        return url.join(odataRootURL, this.path);
      }
    );

    // put the plugin indicator
    return {
      odataRootURL,
    };
  })());
}
